import { useEffect, useRef } from "react";
import type { Editor } from "@tiptap/core";
import { Color } from "@tiptap/extension-color";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import { TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  shouldNavigateOnArrow,
  type CellNavDir,
} from "./cueCellNav";
import {
  shouldArmNewRowAfterEnter,
  shouldCreateRowOnEnter,
} from "./cueEnter";
import {
  contentForEditor,
  isCueContentEmpty,
  looksLikeCueHtml,
  stripTrailingEmptyParagraphs,
} from "./cueRichText";
import { selectQueryOccurrence } from "./findSelect";
import {
  ensureVerticalBandInView,
  scrollEditorSelectionIntoViewSoon,
} from "./scrollIntoView";

export type { CellNavDir };

/** Delete trailing empty paragraphs in place so the caret stays at the end. */
function dropTrailingEmptyParagraphsInEditor(editor: Editor): void {
  const { doc } = editor.state;
  let deleteFrom = doc.content.size;
  for (let i = doc.childCount - 1; i >= 1; i--) {
    const child = doc.child(i);
    if (
      child.type.name !== "paragraph" ||
      child.textContent.trim() !== ""
    ) {
      break;
    }
    deleteFrom -= child.nodeSize;
  }
  if (deleteFrom === doc.content.size) return;
  const tr = editor.state.tr.delete(deleteFrom, doc.content.size);
  editor.view.dispatch(tr);
}

export function CueHtmlEditor({
  content,
  readOnly,
  onChange,
  onFocus,
  onBlur,
  onEditor,
  onNavigate,
  /** Double Return creates a new manuscript row (same as manus view). */
  onNewPair,
  newRowOnDoubleReturn = true,
  focusNonce = 0,
  focusEdge = "end",
  selectQuery,
  selectOccurrence = 0,
  clickSelectsCell = false,
  className,
}: {
  content: string;
  readOnly?: boolean;
  onChange: (html: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onEditor?: (editor: Editor | null) => void;
  onNavigate?: (dir: CellNavDir) => void;
  onNewPair?: (flushedContent: string) => void;
  newRowOnDoubleReturn?: boolean;
  /** Increment to move keyboard focus into this cell. 0 = do not steal focus. */
  focusNonce?: number;
  focusEdge?: "start" | "end";
  /** After focus, select this query occurrence. */
  selectQuery?: string;
  selectOccurrence?: number;
  clickSelectsCell?: boolean;
  className?: string;
}) {
  const onChangeRef = useRef(onChange);
  const onFocusRef = useRef(onFocus);
  const onBlurRef = useRef(onBlur);
  const onEditorRef = useRef(onEditor);
  const onNavigateRef = useRef(onNavigate);
  const onNewPairRef = useRef(onNewPair);
  const newRowOnDoubleReturnRef = useRef(newRowOnDoubleReturn);
  const clickSelectsCellRef = useRef(clickSelectsCell);
  const readOnlyRef = useRef(Boolean(readOnly));
  const contentRef = useRef(content);
  onChangeRef.current = onChange;
  onFocusRef.current = onFocus;
  onBlurRef.current = onBlur;
  onEditorRef.current = onEditor;
  onNavigateRef.current = onNavigate;
  onNewPairRef.current = onNewPair;
  newRowOnDoubleReturnRef.current = newRowOnDoubleReturn;
  clickSelectsCellRef.current = clickSelectsCell;
  readOnlyRef.current = Boolean(readOnly);
  contentRef.current = content;

  const editorRef = useRef<Editor | null>(null);
  /** True from focus until after we have flushed HTML — not editor.isFocused. */
  const focusedRef = useRef(false);
  /** Last HTML from the editor; survives the blur→setContent race with stale props. */
  const liveHtmlRef = useRef(stripTrailingEmptyParagraphs(content || "<p></p>"));
  const pointerModeRef = useRef<"idle" | "selected" | "editing">("idle");
  const newRowArmedRef = useRef(false);
  const empty = isCueContentEmpty(content);

  function flushEditorHtml(ed: Editor): string {
    const html = stripTrailingEmptyParagraphs(ed.getHTML());
    liveHtmlRef.current = html;
    onChangeRef.current(html);
    return html;
  }

  function focusEditorAtClick(
    ed: Editor,
    clientX: number,
    clientY: number,
  ): void {
    if (isCueContentEmpty(contentRef.current)) {
      ed.commands.focus("start", { scrollIntoView: false });
      return;
    }
    const pos = ed.view.posAtCoords({ left: clientX, top: clientY });
    if (pos) {
      ed.chain()
        .focus(undefined, { scrollIntoView: false })
        .setTextSelection(pos.pos)
        .run();
    } else {
      ed.commands.focus("end", { scrollIntoView: false });
    }
  }

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        listKeymap: false,
        code: false,
        horizontalRule: false,
        undoRedo: false,
        trailingNode: false,
      }),
      Underline,
      Superscript,
      Subscript,
      TextStyle,
      Color,
    ],
    content: contentForEditor(content),
    editable: !readOnly,
    immediatelyRender: false,
    editorProps: {
      attributes: { spellcheck: "false" },
      handleScrollToSelection: (view) => {
        try {
          const head = view.state.selection.head;
          const coords = view.coordsAtPos(head);
          ensureVerticalBandInView(
            coords.top,
            coords.bottom,
            view.dom as HTMLElement,
          );
        } catch {
          /* ignore invalid positions during teardown */
        }
        return true;
      },
      handleKeyDown: (_view, event) => {
        const ed = editorRef.current;
        if (!ed || readOnlyRef.current) return false;

        if (event.key === "Enter") {
          if (event.metaKey || event.ctrlKey || event.altKey) {
            newRowArmedRef.current = false;
            return false;
          }
          if (event.shiftKey) {
            newRowArmedRef.current = false;
            ed.commands.splitBlock();
            return true;
          }
          const createRow = onNewPairRef.current;
          if (
            createRow &&
            newRowOnDoubleReturnRef.current &&
            shouldCreateRowOnEnter(ed, newRowArmedRef.current)
          ) {
            event.preventDefault();
            newRowArmedRef.current = false;
            dropTrailingEmptyParagraphsInEditor(ed);
            const flushed = flushEditorHtml(ed);
            createRow(flushed);
            return true;
          }
          newRowArmedRef.current = shouldArmNewRowAfterEnter(ed);
          return false;
        }

        const nav = onNavigateRef.current;
        if (!nav) return false;
        if (event.key === "Tab") {
          event.preventDefault();
          newRowArmedRef.current = false;
          // Flush before focus moves — blur alone can race with setContent.
          flushEditorHtml(ed);
          nav(event.shiftKey ? "left" : "right");
          return true;
        }
        if (
          event.key === "ArrowUp" ||
          event.key === "ArrowDown" ||
          event.key === "ArrowLeft" ||
          event.key === "ArrowRight"
        ) {
          if (event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) {
            return false;
          }
          const dir: CellNavDir =
            event.key === "ArrowUp"
              ? "up"
              : event.key === "ArrowDown"
                ? "down"
                : event.key === "ArrowLeft"
                  ? "left"
                  : "right";
          if (!shouldNavigateOnArrow(ed, dir)) return false;
          event.preventDefault();
          newRowArmedRef.current = false;
          flushEditorHtml(ed);
          nav(dir);
          return true;
        }
        return false;
      },
      handleDOMEvents: {
        mousedown: (_view, event) => {
          if (readOnlyRef.current || event.button !== 0) return false;
          const ed = editorRef.current;
          if (!ed) return false;
          const pickWholeCell = () => {
            pointerModeRef.current = "selected";
            ed.chain()
              .focus(undefined, { scrollIntoView: false })
              .selectAll()
              .run();
          };
          if (event.shiftKey) {
            event.preventDefault();
            pickWholeCell();
            return true;
          }
          if (pointerModeRef.current === "selected") {
            pointerModeRef.current = "editing";
            return false;
          }
          if (clickSelectsCellRef.current && pointerModeRef.current === "idle") {
            event.preventDefault();
            pickWholeCell();
            return true;
          }
          // Empty note cells: any click activates the caret on the first line
          // (same as manuscript), even when the row is taller than one line.
          if (isCueContentEmpty(contentRef.current)) {
            event.preventDefault();
            pointerModeRef.current = "editing";
            ed.commands.focus("start", { scrollIntoView: false });
            return true;
          }
          pointerModeRef.current = "editing";
          return false;
        },
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = stripTrailingEmptyParagraphs(ed.getHTML());
      liveHtmlRef.current = html;
      // Unlocking notes-view manuscript mounts an editor per cell. TipTap may
      // fire onUpdate while hydrating; that must not persist a fake edit.
      if (!focusedRef.current) return;
      onChangeRef.current(html);
    },
    onFocus: ({ editor: ed }) => {
      focusedRef.current = true;
      liveHtmlRef.current = stripTrailingEmptyParagraphs(ed.getHTML());
      onFocusRef.current?.();
      onEditorRef.current?.(ed);
    },
    onBlur: ({ editor: ed }) => {
      newRowArmedRef.current = false;
      // Commit immediately so last keystrokes are not lost to a stale
      // setContent from the parent re-render after focus moves.
      if (!ed.isDestroyed) {
        flushEditorHtml(ed);
      }
      window.setTimeout(() => {
        const active = document.activeElement;
        if (
          active?.closest(".sm-format-toolbar") ||
          active?.closest(".sm-format-color-palette")
        ) {
          // Toolbar mousedown uses preventDefault; if we still land here,
          // keep the cell "live" so further mark toggles keep persisting.
          return;
        }
        focusedRef.current = false;
        if (active?.closest(".ProseMirror")) return;
        pointerModeRef.current = "idle";
        onBlurRef.current?.();
        onEditorRef.current?.(null);
      }, 0);
    },
  });

  editorRef.current = editor;

  useEffect(() => {
    editor?.setEditable(!readOnly);
  }, [editor, readOnly]);

  useEffect(() => {
    if (!editor) return;
    // Keep parent in sync if this cell unmounts while still the live editor
    // (column switch, layout collapse, etc.) without a blur event.
    return () => {
      if (!focusedRef.current) return;
      const ed = editorRef.current;
      if (ed && !ed.isDestroyed) {
        flushEditorHtml(ed);
      } else {
        onChangeRef.current(liveHtmlRef.current);
      }
      focusedRef.current = false;
    };
  }, [editor]);

  useEffect(() => {
    if (!editor || focusedRef.current) return;
    const current = stripTrailingEmptyParagraphs(editor.getHTML());
    const incomingStored = looksLikeCueHtml(content)
      ? stripTrailingEmptyParagraphs(content)
      : null;

    // Editor still holds newer text than props (blur race). Persist it; do not
    // setContent back to the stale prop.
    if (
      incomingStored != null &&
      current === liveHtmlRef.current &&
      current !== incomingStored
    ) {
      onChangeRef.current(current);
      return;
    }
    if (incomingStored != null && current === incomingStored) {
      liveHtmlRef.current = current;
      return;
    }

    editor.commands.setContent(contentForEditor(content), {
      emitUpdate: false,
    });
    liveHtmlRef.current = stripTrailingEmptyParagraphs(editor.getHTML());
  }, [content, editor]);

  const lastFocusNonceRef = useRef(0);
  useEffect(() => {
    if (!editor || !focusNonce) return;
    if (focusNonce === lastFocusNonceRef.current) return;
    lastFocusNonceRef.current = focusNonce;
    if (selectQuery) {
      const ok = selectQueryOccurrence(editor, selectQuery, selectOccurrence);
      if (!ok) {
        editor.commands.focus(focusEdge, { scrollIntoView: false });
      }
    } else {
      editor.commands.focus(focusEdge, { scrollIntoView: false });
    }
    scrollEditorSelectionIntoViewSoon(editor);
  }, [editor, focusNonce, focusEdge, selectQuery, selectOccurrence]);

  return (
    <div
      className={[
        "sm-cue-cell",
        "sm-sources-script-cell",
        empty ? "is-empty" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      onMouseDown={(e) => {
        if (readOnly || !editor || e.button !== 0) return;
        const target = e.target as HTMLElement;
        if (
          target.closest(
            "button, input, textarea, select, label, .sm-format-toolbar",
          )
        ) {
          return;
        }
        // Clicks inside ProseMirror are handled by handleDOMEvents above.
        if (target.closest(".ProseMirror")) return;

        if (e.shiftKey || (clickSelectsCell && pointerModeRef.current === "idle")) {
          e.preventDefault();
          pointerModeRef.current = "selected";
          editor
            .chain()
            .focus(undefined, { scrollIntoView: false })
            .selectAll()
            .run();
          return;
        }
        e.preventDefault();
        pointerModeRef.current = "editing";
        focusEditorAtClick(editor, e.clientX, e.clientY);
      }}
    >
      <EditorContent editor={editor} />
    </div>
  );
}
